import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { GameState, GameActions, GameHelpers } from '../../types/bountifulSea';
import { DiceDisplay } from './DiceDisplay';
import { DeclarationInput } from './DeclarationInput';
import { PlayerHUD, PlayerMini } from './PlayerHUD';
import { BountifulSeaDisplay } from './BountifulSeaDisplay';
import { VoyageTracker } from './VoyageTracker';
import { ScoundrelCallout, CallResolution } from './ScoundrelCallout';

// ═══════════════════════════════════════════════════════════════
// VOYAGE PHASE - MAIN GAMEPLAY COMPONENT
// ═══════════════════════════════════════════════════════════════

interface VoyagePhaseProps {
  state: GameState;
  actions: GameActions;
  helpers: GameHelpers;
}

export const VoyagePhase: React.FC<VoyagePhaseProps> = ({
  state,
  actions,
  helpers,
}) => {
  const currentPlayer = state.players[state.round.currentPlayerId];
  const voyage = currentPlayer?.currentVoyage;
  const isCurrentPlayerAI = currentPlayer?.isAI ?? false;

  // Get other active players for display
  const otherPlayers = helpers.getActivePlayers().filter(
    (p) => p.id !== state.round.currentPlayerId
  );

  // Guard against undefined player
  if (!currentPlayer) {
    return <div>Loading...</div>;
  }

  // Render based on voyage sub-phase
  const renderVoyageContent = () => {
    switch (state.voyageSubPhase) {
      case 'rolling':
        return (
          <motion.div
            key="rolling"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}
          >
            <h3 style={{ color: 'var(--bs-gold)' }}>Rolling the Dice...</h3>
            {voyage && (
              <DiceDisplay
                tideDie={voyage.tideDieValue}
                scoundrelDice={voyage.scoundrelDiceValues}
                showScoundrel={state.showDice && !isCurrentPlayerAI}
                isRolling={state.animatingDice}
                cumulativeTotal={voyage.actualTotal}
                currentHaul={voyage.haul}
              />
            )}
          </motion.div>
        );

      case 'declaring':
        return (
          <motion.div
            key="declaring"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}
          >
            {voyage && !isCurrentPlayerAI && (
              <>
                <DiceDisplay
                  tideDie={voyage.tideDieValue}
                  scoundrelDice={voyage.scoundrelDiceValues}
                  showScoundrel={state.showDice}
                  cumulativeTotal={voyage.actualTotal}
                  currentHaul={voyage.haul}
                />
                <DeclarationInput
                  minValue={helpers.getMinDeclaration()}
                  maxValue={helpers.getMaxPossibleRoll()}
                  currentRoll={voyage.tideDieValue + voyage.scoundrelDiceValues.reduce((a, b) => a + b, 0)}
                  haul={voyage.haul}
                  onDeclare={actions.declareTotal}
                  disabled={state.animatingDice}
                />
                {helpers.mustSinkOrLie() && (
                  <motion.button
                    className="bs-btn bs-btn-sink"
                    onClick={actions.sink}
                    whileHover={{ scale: 1.02 }}
                  >
                    Sink (Return haul to sea)
                  </motion.button>
                )}
              </>
            )}
            {isCurrentPlayerAI && (
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: 'var(--bs-text-secondary)' }}>
                  {currentPlayer.name} is considering their declaration...
                </p>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  style={{ fontSize: '2rem', marginTop: '1rem' }}
                >
                  🤔
                </motion.div>
              </div>
            )}
          </motion.div>
        );

      case 'awaitingCalls':
        return (
          <motion.div
            key="awaitingCalls"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}
          >
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <p style={{ color: 'var(--bs-text-secondary)' }}>
                {currentPlayer.name} declares:
              </p>
              <p style={{
                fontFamily: 'var(--bs-font-heading)',
                fontSize: '1.5rem',
                color: 'var(--bs-gold)',
              }}>
                "The Bountiful Sea grants me {voyage?.declaredTotal} coins!"
              </p>
            </div>

            <ScoundrelCallout
              callablePlayers={helpers.getCallablePlayers()}
              targetPlayer={currentPlayer}
              onCall={actions.callScoundrel}
              onPass={actions.passCall}
            />
          </motion.div>
        );

      case 'callResolution':
        if (!state.pendingCall || !voyage) return null;
        const caller = state.players[state.pendingCall.callerId];
        if (!caller) return null;

        return (
          <motion.div
            key="callResolution"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CallResolution
              call={state.pendingCall}
              targetPlayer={currentPlayer}
              callerPlayer={caller}
              voyage={{
                tideDieValue: voyage.tideDieValue,
                scoundrelDiceValues: voyage.scoundrelDiceValues,
                actualTotal: voyage.actualTotal,
                declaredTotal: voyage.declaredTotal!,
              }}
              onContinue={actions.resolveCall}
            />
          </motion.div>
        );

      case 'decision':
        return (
          <motion.div
            key="decision"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}
          >
            {!isCurrentPlayerAI && (
              <>
                <h3 style={{ color: 'var(--bs-gold)' }}>
                  Sail On or Make Port?
                </h3>
                <p style={{ color: 'var(--bs-text-secondary)', textAlign: 'center' }}>
                  Current haul: <strong style={{ color: 'var(--bs-gold)' }}>{voyage?.haul}</strong> coins
                  <br />
                  Pot remaining: <strong style={{ color: 'var(--bs-sea-teal)' }}>{state.bountifulSea}</strong> coins
                </p>

                <div className="bs-decision">
                  <motion.button
                    className="bs-btn bs-btn-sail bs-btn-large"
                    onClick={actions.sailOn}
                    disabled={state.bountifulSea <= 0}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ⛵ Sail On
                  </motion.button>
                  <motion.button
                    className="bs-btn bs-btn-port bs-btn-large"
                    onClick={actions.makePort}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ⚓ Make Port
                  </motion.button>
                </div>

                {state.bountifulSea <= 0 && (
                  <p style={{ color: 'var(--bs-warning)', fontSize: '0.9rem' }}>
                    The Bountiful Sea is empty! You must make port.
                  </p>
                )}
              </>
            )}
            {isCurrentPlayerAI && (
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: 'var(--bs-text-secondary)' }}>
                  {currentPlayer.name} is deciding...
                </p>
              </div>
            )}
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bs-voyage">
      {/* Header with round info */}
      <div className="bs-voyage-header">
        <div className="bs-voyage-info">
          <span className="bs-round-info">Round {state.round.roundNumber}</span>
          <span style={{ color: currentPlayer?.color, fontFamily: 'var(--bs-font-heading)' }}>
            {currentPlayer?.name}'s Voyage
          </span>
        </div>
        <PlayerHUD player={currentPlayer} isActive showVoyageInfo />
      </div>

      {/* Main 3-column layout */}
      <div className="bs-voyage-main">
        {/* Left: Voyage Tracker */}
        <div className="bs-voyage-left">
          <VoyageTracker voyage={voyage || null} />

          {/* Other players list */}
          <div className="bs-players-list">
            <div className="bs-players-title">Other Pirates</div>
            {otherPlayers.map((player) => (
              <PlayerMini key={player.id} player={player} />
            ))}
          </div>
        </div>

        {/* Center: Pot and main action */}
        <div className="bs-voyage-center">
          <BountifulSeaDisplay coins={state.bountifulSea} />
        </div>

        {/* Right: Main voyage content */}
        <div className="bs-voyage-right" style={{ minWidth: '300px' }}>
          <AnimatePresence mode="wait">
            {renderVoyageContent()}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default VoyagePhase;
